/**
 * Voice note file (v1).
 *
 * The canonical, runtime-neutral wire format for one recorded voice note: the
 * audio bytes plus the cursor / viewport track captured while recording. One
 * file per note is both the storage format (materialized into
 * `annotation_files` under `voice_note_cbor`) and the export format; see
 * docs/voice-note-file-v1.md for the normative specification.
 *
 * Encoding: one CBOR map (RFC 8949) with text keys. Every numeric column is a
 * CBOR byte string of little-endian values (`t` uint32 ms, everything else
 * float32) so the file needs no CBOR tags and decodes identically everywhere.
 *
 * Time base: milliseconds of recorded audio (paused time excluded), so
 * `audio.currentTime * 1000` indexes the track directly. A pointer sample with
 * NaN coordinates means the pointer left the canvas at that time.
 */
import { decode, encode } from "cbor-x"
import { z } from "zod"

export const VOICE_NOTE_FILE_VERSION = 1 as const
/** The one codec every browser records; see AGENTS.md "Voice Notes". */
export const VOICE_NOTE_MIME_TYPE = "audio/webm;codecs=opus" as const
/**
 * Only the WebM container is ever stored (playback wraps the bytes as WebM);
 * the codec parameter is optional because some recorders report `audio/webm`.
 */
const VOICE_NOTE_MIME_PATTERN = /^audio\/webm(;.*)?$/i
/**
 * Neighbouring samples further apart than this are not interpolated: the
 * pointer / camera simply held its last position (samples are only emitted on
 * movement).
 */
export const VOICE_NOTE_MAX_INTERPOLATION_GAP_MS = 250

export interface VoiceNotePointerTrack {
  /** Milliseconds of recorded audio, ascending. */
  t: Uint32Array
  /** Level-0 slide pixels; NaN pairs mark "pointer left the canvas". */
  x: Float32Array
  y: Float32Array
}

export interface VoiceNoteViewportTrack {
  t: Uint32Array
  zoom: Float32Array
  /** Viewport center in level-0 slide pixels. */
  cx: Float32Array
  cy: Float32Array
}

export interface VoiceNoteFileV1 {
  version: typeof VOICE_NOTE_FILE_VERSION
  /** Base slide series the note was recorded on. */
  seriesId: string
  imageWidth: number
  imageHeight: number
  startedAt: string
  durationMs: number
  mimeType: string
  title: string | null
  caption: string | null
  audio: Uint8Array
  pointer: VoiceNotePointerTrack
  viewport: VoiceNoteViewportTrack
}

const bytesSchema = z.instanceof(Uint8Array)

const wireSchema = z.object({
  version: z.literal(VOICE_NOTE_FILE_VERSION),
  seriesId: z.string().min(1),
  imageWidth: z.number().int().min(1),
  imageHeight: z.number().int().min(1),
  startedAt: z.string().min(1),
  durationMs: z.number().int().min(0),
  mimeType: z.string().regex(VOICE_NOTE_MIME_PATTERN, "mimeType must be audio/webm"),
  title: z.string().nullable(),
  caption: z.string().nullable(),
  audio: bytesSchema,
  pointer: z.object({ t: bytesSchema, x: bytesSchema, y: bytesSchema }),
  viewport: z.object({ t: bytesSchema, zoom: bytesSchema, cx: bytesSchema, cy: bytesSchema })
})

function u32ToBytes(values: Uint32Array): Uint8Array {
  const out = new Uint8Array(values.length * 4)
  const view = new DataView(out.buffer)
  for (let i = 0; i < values.length; i++) view.setUint32(i * 4, values[i] as number, true)
  return out
}

function f32ToBytes(values: Float32Array): Uint8Array {
  const out = new Uint8Array(values.length * 4)
  const view = new DataView(out.buffer)
  for (let i = 0; i < values.length; i++) view.setFloat32(i * 4, values[i] as number, true)
  return out
}

function bytesToU32(bytes: Uint8Array): Uint32Array {
  const out = new Uint32Array(bytes.byteLength / 4)
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  for (let i = 0; i < out.length; i++) out[i] = view.getUint32(i * 4, true)
  return out
}

function bytesToF32(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.byteLength / 4)
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  for (let i = 0; i < out.length; i++) out[i] = view.getFloat32(i * 4, true)
  return out
}

function columnsConsistent(columns: Uint8Array[]): boolean {
  const first = columns[0]?.byteLength ?? 0
  return first % 4 === 0 && columns.every((c) => c.byteLength === first)
}

/** Every sample must be a finite center and a finite, positive zoom. */
function viewportSane(v: VoiceNoteViewportTrack): boolean {
  for (let i = 0; i < v.t.length; i++) {
    const zoom = v.zoom[i] as number
    if (!(Number.isFinite(zoom) && zoom > 0)) return false
    if (!(Number.isFinite(v.cx[i] as number) && Number.isFinite(v.cy[i] as number))) return false
  }
  return true
}

function ascending(t: Uint32Array): boolean {
  for (let i = 1; i < t.length; i++) {
    if ((t[i] as number) < (t[i - 1] as number)) return false
  }
  return true
}

export function encodeVoiceNoteFile(file: VoiceNoteFileV1): Uint8Array {
  // Untyped on purpose: zod infers Uint8Array<ArrayBuffer> for instanceof, the
  // column encoders return Uint8Array<ArrayBufferLike>; cbor-x accepts either.
  const wire = {
    version: VOICE_NOTE_FILE_VERSION,
    seriesId: file.seriesId,
    imageWidth: file.imageWidth,
    imageHeight: file.imageHeight,
    startedAt: file.startedAt,
    durationMs: file.durationMs,
    mimeType: file.mimeType,
    title: file.title,
    caption: file.caption,
    audio: file.audio,
    pointer: { t: u32ToBytes(file.pointer.t), x: f32ToBytes(file.pointer.x), y: f32ToBytes(file.pointer.y) },
    viewport: {
      t: u32ToBytes(file.viewport.t),
      zoom: f32ToBytes(file.viewport.zoom),
      cx: f32ToBytes(file.viewport.cx),
      cy: f32ToBytes(file.viewport.cy)
    }
  }
  return encode(wire)
}

/** Decode + validate; returns null on any structural problem. */
export function decodeVoiceNoteFile(bytes: Uint8Array): VoiceNoteFileV1 | null {
  try {
    const parsed = wireSchema.safeParse(decode(bytes))
    if (!parsed.success) return null
    const w = parsed.data
    if (!columnsConsistent([w.pointer.t, w.pointer.x, w.pointer.y])) return null
    if (!columnsConsistent([w.viewport.t, w.viewport.zoom, w.viewport.cx, w.viewport.cy])) return null
    const pointer: VoiceNotePointerTrack = {
      t: bytesToU32(w.pointer.t),
      x: bytesToF32(w.pointer.x),
      y: bytesToF32(w.pointer.y)
    }
    const viewport: VoiceNoteViewportTrack = {
      t: bytesToU32(w.viewport.t),
      zoom: bytesToF32(w.viewport.zoom),
      cx: bytesToF32(w.viewport.cx),
      cy: bytesToF32(w.viewport.cy)
    }
    if (!(ascending(pointer.t) && ascending(viewport.t))) return null
    if (!viewportSane(viewport)) return null
    return {
      version: VOICE_NOTE_FILE_VERSION,
      seriesId: w.seriesId,
      imageWidth: w.imageWidth,
      imageHeight: w.imageHeight,
      startedAt: w.startedAt,
      durationMs: w.durationMs,
      mimeType: w.mimeType,
      title: w.title,
      caption: w.caption,
      // A view into the decoded buffer: audio can be tens of MB, never copy it.
      audio: new Uint8Array(w.audio.buffer, w.audio.byteOffset, w.audio.byteLength),
      pointer,
      viewport
    }
  } catch {
    return null
  }
}

/** Index of the last sample with `t[i] <= tMs`, or -1 when `tMs` precedes the track. */
export function findSampleIndex(t: Uint32Array, tMs: number): number {
  let lo = 0
  let hi = t.length - 1
  let found = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if ((t[mid] as number) <= tMs) {
      found = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return found
}

function interpolationWeight(t: Uint32Array, i: number, tMs: number): number | null {
  if (i + 1 >= t.length) return null
  const t0 = t[i] as number
  const t1 = t[i + 1] as number
  if (t1 - t0 > VOICE_NOTE_MAX_INTERPOLATION_GAP_MS || t1 === t0) return null
  return (tMs - t0) / (t1 - t0)
}

/**
 * Pointer position at `tMs`, or null when the pointer was off the canvas or
 * the recording had not reached the first sample yet. Holds the last position
 * across gaps (no samples = no movement) and interpolates between close
 * neighbours so playback looks continuous at 60 fps.
 */
export function samplePointerAt(file: Pick<VoiceNoteFileV1, "pointer">, tMs: number): { x: number; y: number } | null {
  const { t, x, y } = file.pointer
  const i = findSampleIndex(t, tMs)
  if (i < 0) return null
  const x0 = x[i] as number
  const y0 = y[i] as number
  if (Number.isNaN(x0) || Number.isNaN(y0)) return null
  const w = interpolationWeight(t, i, tMs)
  if (w === null) return { x: x0, y: y0 }
  const x1 = x[i + 1] as number
  const y1 = y[i + 1] as number
  if (Number.isNaN(x1) || Number.isNaN(y1)) return { x: x0, y: y0 }
  return { x: x0 + (x1 - x0) * w, y: y0 + (y1 - y0) * w }
}

/**
 * Viewport at `tMs`. Before the first sample the initial viewport is returned
 * so "follow camera" can position the view at the very start of a note; null
 * only when the track is empty.
 */
export function sampleViewportAt(
  file: Pick<VoiceNoteFileV1, "viewport">,
  tMs: number
): { zoom: number; cx: number; cy: number } | null {
  const { t, zoom, cx, cy } = file.viewport
  if (t.length === 0) return null
  const i = Math.max(0, findSampleIndex(t, tMs))
  const z0 = zoom[i] as number
  const x0 = cx[i] as number
  const y0 = cy[i] as number
  const w = i === 0 && tMs < (t[0] as number) ? null : interpolationWeight(t, i, tMs)
  if (w === null) return { zoom: z0, cx: x0, cy: y0 }
  const z1 = zoom[i + 1] as number
  return {
    // Zoom is perceptually logarithmic, so blend it in log space.
    zoom: Math.exp(Math.log(z0) + (Math.log(z1) - Math.log(z0)) * w),
    cx: x0 + ((cx[i + 1] as number) - x0) * w,
    cy: y0 + ((cy[i + 1] as number) - y0) * w
  }
}

/** Growable typed-array columns used while recording; `finish()` trims them. */
export class VoiceNoteTrackBuilder {
  private pointerT: number[] = []
  private pointerX: number[] = []
  private pointerY: number[] = []
  private viewportT: number[] = []
  private viewportZoom: number[] = []
  private viewportCx: number[] = []
  private viewportCy: number[] = []

  get pointerSampleCount(): number {
    return this.pointerT.length
  }

  pushPointer(tMs: number, x: number, y: number): void {
    this.pointerT.push(Math.max(0, Math.round(tMs)))
    this.pointerX.push(x)
    this.pointerY.push(y)
  }

  pushPointerLeave(tMs: number): void {
    this.pushPointer(tMs, Number.NaN, Number.NaN)
  }

  pushViewport(tMs: number, zoom: number, cx: number, cy: number): void {
    this.viewportT.push(Math.max(0, Math.round(tMs)))
    this.viewportZoom.push(zoom)
    this.viewportCx.push(cx)
    this.viewportCy.push(cy)
  }

  finish(): { pointer: VoiceNotePointerTrack; viewport: VoiceNoteViewportTrack } {
    return {
      pointer: {
        t: Uint32Array.from(this.pointerT),
        x: Float32Array.from(this.pointerX),
        y: Float32Array.from(this.pointerY)
      },
      viewport: {
        t: Uint32Array.from(this.viewportT),
        zoom: Float32Array.from(this.viewportZoom),
        cx: Float32Array.from(this.viewportCx),
        cy: Float32Array.from(this.viewportCy)
      }
    }
  }
}
